import { useEffect, useRef, useState } from "react";

import type { MorrowAccountType, MorrowQuestionnaire } from "../onboardingStore";
import {
  fetchMorrowGreeting,
  getMorrowGreetingRecord,
  markMorrowGreetingGaveUp,
  writeMorrowGreetingRecord,
} from "../morrowGreeting";

/**
 * Drives the proactive "app speaks first" greeting on the empty-chat landing.
 *
 *  - `disabled` — not eligible (guest, onboarding unfinished, welcome thread
 *    already seeded, or a thread that already has messages). Caller shows the
 *    normal static landing.
 *  - `loading`  — the server is building the user's profile (~1–2 min). Caller
 *    shows the animated "getting to know you" state.
 *  - `ready`    — greeting + suggestion chips are in hand; the caller reveals
 *    them in place on the landing (the "here's what I know about you" moment,
 *    with the suggested tasks below).
 *  - `gaveup`   — we polled past the cap without a profile. Caller silently
 *    falls back to the normal static landing (no error surfaced).
 */
export type MorrowGreetingStatus = "disabled" | "loading" | "ready" | "gaveup";

export interface MorrowGreetingState {
  readonly status: MorrowGreetingStatus;
  readonly greeting: string;
  readonly suggestions: readonly string[];
}

export interface UseMorrowGreetingOptions {
  /** Only true once onboarding is done, the user is signed in, and this is the empty draft. */
  readonly enabled: boolean;
  /** Stable per-user id (Clerk user id when signed in). */
  readonly userId: string;
  /**
   * Server truth: the welcome thread (the knowledge record) already exists for
   * this account. When true the loader stays `disabled` — we've already greeted
   * and seeded, on any device. This is the cross-session gate that replaced the
   * fragile up-front localStorage marker (which died on remount and killed the
   * poll before the record was ever created).
   */
  readonly knowledgeReady: boolean;
  /** Active Clerk org id (`org_…`) or null — routes the org profile to the org doc. */
  readonly orgId: string | null;
  readonly accountType: MorrowAccountType | null;
  readonly companyName: string;
  /** Company domain + looked-up brief/focus, so the greeting can speak to their business. */
  readonly companyWebsite: string;
  readonly companyBrief: string;
  readonly companySituation: string;
  readonly toolkits: ReadonlySet<string>;
  readonly answers: MorrowQuestionnaire;
}

const DISABLED_STATE: MorrowGreetingState = { status: "disabled", greeting: "", suggestions: [] };

const POLL_INTERVAL_MS = 3_000;
const MAX_WAIT_MS = 3.5 * 60 * 1000; // ~3.5 min, then quietly fall back.

/** Drop the optional/undefined questionnaire fields into a flat string map for the request body. */
function questionnaireToAnswers(questionnaire: MorrowQuestionnaire): Record<string, string> {
  const answers: Record<string, string> = {};
  for (const [key, value] of Object.entries(questionnaire)) {
    if (typeof value === "string" && value.trim().length > 0) answers[key] = value;
  }
  return answers;
}

export function useMorrowGreeting(options: UseMorrowGreetingOptions): MorrowGreetingState {
  const { enabled, userId, knowledgeReady } = options;
  const [state, setState] = useState<MorrowGreetingState>(DISABLED_STATE);

  // Read the request payload through a ref so the poll fires (and restarts) only
  // when eligibility or the user changes — not each time the connected-toolkit
  // set repopulates a moment after mount, which would otherwise reset the clock.
  const payloadRef = useRef(options);
  payloadRef.current = options;

  useEffect(() => {
    if (!enabled || !userId) {
      setState(DISABLED_STATE);
      return;
    }

    // Cross-session gate, server-authoritative: if the welcome thread (knowledge
    // record) already exists, we've greeted before — stay disabled. This is the
    // DB truth, so it holds across devices and can't be lost like localStorage.
    if (knowledgeReady) {
      setState(DISABLED_STATE);
      return;
    }

    // Failure-path bound only: if a prior run polled the full window without the
    // server ever seeding a thread, it left a "gave up" marker so we don't loop
    // the loader forever. The happy path writes NOTHING up front — that's what
    // lets the loader survive a remount and keep polling until the record exists.
    if (getMorrowGreetingRecord(userId)) {
      setState(DISABLED_STATE);
      return;
    }

    let cancelled = false;
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout> | null = null;
    const startedAt = Date.now();
    setState({ status: "loading", greeting: "", suggestions: [] });

    const poll = async () => {
      const current = payloadRef.current;
      const result = await fetchMorrowGreeting(
        {
          userId,
          companyName: current.companyName,
          companyWebsite: current.companyWebsite,
          companyBrief: current.companyBrief,
          companySituation: current.companySituation,
          ...(current.orgId ? { orgId: current.orgId } : {}),
          ...(current.accountType ? { accountType: current.accountType } : {}),
          toolkits: [...current.toolkits],
          answers: questionnaireToAnswers(current.answers),
        },
        controller.signal,
      );
      if (cancelled) return;

      if (result?.ready) {
        // Reveal the greeting + suggestions in place on the landing — the app's
        // "here's what I know about you" moment. Persist the seeded thread +
        // suggestions so the reveal survives a reload within the session; the
        // durable cross-session gate is the server's `knowledgeReady`.
        if (result.threadId) {
          writeMorrowGreetingRecord(userId, {
            threadId: result.threadId,
            suggestions: result.suggestions,
          });
        } else {
          // Ready but no thread seeded — don't re-loop the loader next draft.
          markMorrowGreetingGaveUp(userId);
        }
        setState({
          status: "ready",
          greeting: result.greeting,
          suggestions: result.suggestions,
        });
        return;
      }

      if (Date.now() - startedAt >= MAX_WAIT_MS) {
        // Terminal give-up: bound the failure case so a never-ready server does
        // not replay the loader on every future new chat.
        markMorrowGreetingGaveUp(userId);
        setState({ status: "gaveup", greeting: "", suggestions: [] });
        return;
      }
      timer = setTimeout(() => void poll(), POLL_INTERVAL_MS);
    };

    void poll();

    return () => {
      cancelled = true;
      controller.abort();
      if (timer) clearTimeout(timer);
    };
    // Keyed on eligibility + user + the knowledge-ready gate (payload via ref).
  }, [enabled, userId, knowledgeReady]);

  return state;
}
